import { useState, useRef } from "react";
import type { Question, QuestionTracker } from "@shikaku/shared";
import { ProgressBar } from "../quiz/ProgressBar";
import { ExplanationCard } from "../quiz/ExplanationCard";

interface MemorizeViewProps {
  questions: Question[];
  tracker: QuestionTracker;
  accentColor: string;
  onBack: () => void;
}

export function MemorizeView({
  questions,
  tracker,
  accentColor,
  onBack,
}: MemorizeViewProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isRevealed, setIsRevealed] = useState(false);
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startXRef = useRef(0);

  const q = currentIndex < questions.length ? questions[currentIndex] : null;
  const progress = questions.length > 0 ? currentIndex / questions.length : 0;
  const progressLabel = `${currentIndex + 1} / ${questions.length}`;
  const isLast = currentIndex >= questions.length - 1;

  function swipeLeft() {
    if (!q) return;
    if (!tracker.isBookmarked(q.id)) tracker.toggleBookmark(q.id);
    advance();
  }

  function swipeRight() {
    if (!q) return;
    if (tracker.isBookmarked(q.id)) tracker.toggleBookmark(q.id);
    advance();
  }

  function advance() {
    setDragX(0);
    setIsRevealed(false);
    if (isLast) { onBack(); } else { setCurrentIndex((i) => i + 1); }
  }

  const handlePointerDown = (e: React.PointerEvent) => {
    startXRef.current = e.clientX;
    setIsDragging(true);
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging) return;
    setDragX(e.clientX - startXRef.current);
  };

  const handlePointerUp = () => {
    if (!isDragging) return;
    setIsDragging(false);
    if (dragX > 100) swipeRight();
    else if (dragX < -100) swipeLeft();
    else setDragX(0);
  };

  const opacity = (target: "right" | "left") => {
    if (target === "right") return Math.min(Math.max(dragX / 100, 0), 1);
    return Math.min(Math.max(-dragX / 100, 0), 1);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden", background: "#f5f5f5" }}>
      {/* Header */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e8e8e8", padding: "12px 20px", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <button
            onClick={onBack}
            style={{ color: accentColor, fontSize: 14, fontWeight: 600, cursor: "pointer", background: "none", border: "none", padding: 0 }}
          >
            ← 戻る
          </button>
          <span style={{ fontSize: 14, fontWeight: 700, color: "#333" }}>暗記モード</span>
          <span style={{ fontSize: 13, color: "#888" }}>{progressLabel}</span>
        </div>
      </div>

      <div style={{ flexShrink: 0 }}>
        <ProgressBar progress={progress} label={`カード ${progressLabel}`} accentColor={accentColor} />
      </div>

      {q ? (
        <>
          {/* Card area */}
          <div style={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 20px", position: "relative", overflow: "hidden" }}>
            {/* Current card */}
            <div
              style={{
                position: "relative",
                width: "100%",
                maxWidth: 500,
                maxHeight: "60vh",
                overflowY: "auto",
                background: "#fff",
                border: "1px solid #e0e0e0",
                borderRadius: 8,
                padding: 24,
                cursor: isDragging ? "grabbing" : "grab",
                transform: `translateX(${dragX}px) rotate(${dragX / 20}deg)`,
                transition: isDragging ? "none" : "transform 0.25s ease",
                zIndex: 1,
              }}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
            >
              {/* Swipe overlays */}
              <div style={{
                position: "absolute", left: 16, top: 16,
                border: "3px solid #2ecc71", borderRadius: 8,
                padding: "4px 16px", fontSize: 22, fontWeight: 800,
                color: "#2ecc71", transform: "rotate(-12deg)",
                opacity: opacity("right"), pointerEvents: "none",
              }}>
                覚えた
              </div>
              <div style={{
                position: "absolute", right: 16, top: 16,
                border: "3px solid #ff5a7e", borderRadius: 8,
                padding: "4px 16px", fontSize: 22, fontWeight: 800,
                color: "#ff5a7e", transform: "rotate(12deg)",
                opacity: opacity("left"), pointerEvents: "none",
              }}>
                覚えてない
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "#888", background: "#f5f5f5", padding: "4px 10px", borderRadius: 4 }}>
                  {q.unit}
                </span>
                <button
                  onClick={(e) => { e.stopPropagation(); tracker.toggleBookmark(q.id); }}
                  style={{ background: "none", border: "none", cursor: "pointer", color: tracker.isBookmarked(q.id) ? "#f0a030" : "#ccc" }}
                >
                  <svg width="20" height="20" fill={tracker.isBookmarked(q.id) ? "currentColor" : "none"} stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                  </svg>
                </button>
              </div>

              <p style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.7, color: "#333", marginTop: 16 }}>
                {q.memo ?? q.question}
              </p>

              {isRevealed ? (
                <div className="animate-fade-in" style={{ marginTop: 16 }}>
                  {!q.memo && (
                    <div style={{ background: "#e8f8ef", border: "2px solid #2ecc71", borderRadius: 8, padding: 14, fontSize: 14, fontWeight: 600, color: "#1a7d45", marginBottom: 12 }}>
                      ✓ {q.choices[q.answer]}
                    </div>
                  )}
                  <ExplanationCard text={q.explanation} accentColor={accentColor} />
                </div>
              ) : (
                <button
                  onClick={(e) => { e.stopPropagation(); setIsRevealed(true); }}
                  style={{
                    width: "100%", marginTop: 20, padding: 12,
                    background: `${accentColor}10`, border: `2px solid ${accentColor}30`,
                    borderRadius: 8, cursor: "pointer",
                    fontSize: 14, fontWeight: 600, color: accentColor,
                  }}
                >
                  {q.memo ? "解説を見る" : "答えを見る"}
                </button>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div style={{ flexShrink: 0, display: "flex", justifyContent: "center", gap: 48, padding: "20px 0", background: "#fff", borderTop: "1px solid #e8e8e8" }}>
            <button onClick={swipeLeft} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer" }}>
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#ff5a7e", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="24" height="24" fill="none" stroke="#fff" strokeWidth="3" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#888" }}>覚えてない</span>
            </button>
            <button onClick={swipeRight} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer" }}>
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#2ecc71", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="24" height="24" fill="none" stroke="#fff" strokeWidth="3" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#888" }}>覚えた</span>
            </button>
          </div>
        </>
      ) : (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#999", fontSize: 15 }}>
          問題がありません
        </div>
      )}
    </div>
  );
}
